import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Endpoint } from '@/Config/api/endpoint';
import { QueryKey } from '@/Config/api/queryKey';
import {
  DEV_QUICKCHAT_SUPPORT_BOT_SCENARIO_ID,
  QUICKCHAT_SUPPORT_BOT_SCENARIO_ID,
} from '@/Config/scenarioIds';
import { User } from '@/Domain/api/User/dto/User.dto';
import { useWidgetLocationSync } from '@/Domain/components/App/useWidgetLocationSync';
import { useSupportWidgetStore } from '@/Domain/store/supportWidget.store';
import { getCurrentScenarioId } from '@/context';

const body = document.querySelector('body');

const PROD_ASSISTANT_HOST = 'https://app.quickchat.ai';

const DEV_ASSISTANT_HOST = `https://${body?.getAttribute('api_server_url')}`;

const ASSISTANT_SCENARIO_ID = import.meta.env.DEV
  ? DEV_QUICKCHAT_SUPPORT_BOT_SCENARIO_ID
  : QUICKCHAT_SUPPORT_BOT_SCENARIO_ID;
const ASSISTANT_HOST = import.meta.env.DEV ? DEV_ASSISTANT_HOST : PROD_ASSISTANT_HOST;

const WIDGET_ORIGIN = import.meta.env.VITE_WIDGET_URL || 'https://bubble.quickchat.ai';
const SUPPORT_IDENTITY_MESSAGE = 'quickchat_support_identity';
const SUPPORT_IDENTITY_READY_MESSAGE = 'quickchat_support_identity_ready';

// Re-mint before the server's 60-minute token TTL.
const TOKEN_REFETCH_INTERVAL_MS = 45 * 60 * 1000;

export const SupportWidget = () => {
  const queryClient = useQueryClient();

  const myself = queryClient.getQueryData<User>([Endpoint.user.myself]);
  const { open, toggleOpen, pendingMessage, clearPendingMessage } = useSupportWidgetStore();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const paramsReadyRef = useRef(false);
  const supportIdentityReadyRef = useRef(false);

  const { data: supportIdentityToken } = useQuery<{ token: string }>({
    queryKey: [QueryKey.app.supportIdentityToken, myself?.id],
    enabled: Boolean(myself),
    staleTime: TOKEN_REFETCH_INTERVAL_MS,
    refetchInterval: TOKEN_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });

  const location = useLocation();
  const currentScenarioId = useMemo(() => getCurrentScenarioId(), [location.pathname]);

  const clientMetadata = useMemo(() => {
    if (myself) {
      const visitor_name = [myself.first_name, myself.last_name].filter(Boolean).join(' ').trim();

      return {
        visitor_name: visitor_name || '',
        visitor_email: myself.email,
        // Later route changes are pushed without reloading the iframe.
        visitor_scenario_id: getCurrentScenarioId(),
      };
    }

    return null;
  }, [myself]);

  const config = useMemo(() => {
    if (!clientMetadata) return null;

    const params = new URLSearchParams({
      scenarioId: ASSISTANT_SCENARIO_ID,
      viewType: 'embed',
      showCloseButton: 'true',
      // Suppress the widget's autofocus on iframe load. The Support AI iframe
      // is mounted globally by `DefaultLayout` and finishes loading 1-2s after
      // any page-level autofocus (e.g. the Getting Started URL input), at
      // which point its `MessageInput` would steal focus from the parent app.
      autoFocus: 'false',
      ...(ASSISTANT_HOST ? { host: ASSISTANT_HOST } : {}),
      params: JSON.stringify(clientMetadata),
    });

    return `${WIDGET_ORIGIN}/widget.html?${params.toString()}`;
  }, [clientMetadata]);

  const postCustomParams = useCallback((data: Record<string, string>) => {
    if (!paramsReadyRef.current) return;
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'quickchat_update_custom_params', data },
      WIDGET_ORIGIN,
    );
  }, []);

  const postSupportIdentity = useCallback((token: string) => {
    if (!supportIdentityReadyRef.current) return;
    iframeRef.current?.contentWindow?.postMessage(
      { type: SUPPORT_IDENTITY_MESSAGE, data: { token } },
      WIDGET_ORIGIN,
    );
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.data?.type === 'quickchat_minimize') {
        toggleOpen();
      } else if (event.data?.type === 'quickchat_custom_params_ready') {
        paramsReadyRef.current = true;
        postCustomParams({ visitor_scenario_id: currentScenarioId });
      } else if (event.data?.type === SUPPORT_IDENTITY_READY_MESSAGE) {
        supportIdentityReadyRef.current = true;
        if (supportIdentityToken?.token) postSupportIdentity(supportIdentityToken.token);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [
    toggleOpen,
    postCustomParams,
    postSupportIdentity,
    supportIdentityToken?.token,
    currentScenarioId,
  ]);

  // Keep the widget's in-memory credential current for every send.
  useEffect(() => {
    if (supportIdentityToken?.token) {
      postSupportIdentity(supportIdentityToken.token);
    }
  }, [postSupportIdentity, supportIdentityToken?.token]);

  useEffect(() => {
    postCustomParams({ visitor_scenario_id: currentScenarioId });
  }, [postCustomParams, currentScenarioId]);

  useEffect(() => {
    if (!open || !pendingMessage || !iframeRef.current) return;

    const iframe = iframeRef.current;

    const sendMessage = () => {
      iframe.contentWindow?.postMessage(
        { type: 'quickchat_send_message', data: { message: pendingMessage } },
        WIDGET_ORIGIN,
      );
      clearPendingMessage();
    };

    if (iframe.dataset.loaded) {
      sendMessage();
    } else {
      iframe.addEventListener('load', sendMessage, { once: true });
      return () => iframe.removeEventListener('load', sendMessage);
    }
  }, [open, pendingMessage, clearPendingMessage]);

  const handleIframeLoad = useCallback(() => {
    if (iframeRef.current) {
      iframeRef.current.dataset.loaded = 'true';
    }
  }, []);

  useWidgetLocationSync(iframeRef, WIDGET_ORIGIN);

  if (!myself || !clientMetadata || !config) return null;

  return (
    <iframe
      ref={iframeRef}
      id="feedback-quickchat-widget"
      title="Feedback QuickChat Widget"
      src={config}
      onLoad={handleIframeLoad}
      allow="microphone; local-network-access"
      referrerPolicy="strict-origin"
      className={`fixed bottom-0 right-0 z-50 m-[16px] h-[calc(100%-75px)] w-full max-w-[420px] rounded-xl border border-border bg-card duration-300 shadow-xl ${
        open
          ? 'ease-in-out translate-x-0 opacity-100 scale-100 visible'
          : 'ease-in-out translate-x-full opacity-70 scale-20 invisible'
      }`}
    />
  );
};
